import React from 'react';

interface LexicalRendererProps {
  value: string;
  className?: string;
}

const LexicalRenderer: React.FC<LexicalRendererProps> = ({ value, className = '' }) => {
  const renderLexicalContent = (lexicalJson: string): React.ReactNode => {
    try {
      const parsed = JSON.parse(lexicalJson);
      if (parsed.root && parsed.root.children) {
        return renderChildren(parsed.root.children);
      }
    } catch (error) {
      // If parsing fails, return the original string as plain text
      return <span>{lexicalJson}</span>;
    }
    return <span>{lexicalJson}</span>;
  };

  const renderChildren = (children: any[]): React.ReactNode => {
    return children.map((child, index) => renderNode(child, index));
  };

  const renderNode = (node: any, key: number): React.ReactNode => {
    switch (node.type) {
      case 'paragraph':
        return (
          <p key={key} className="mb-2 last:mb-0">
            {node.children ? renderChildren(node.children) : null}
          </p>
        );
      
      case 'text':
        let content: React.ReactNode = node.text || '';
        
        // Apply text formatting
        if (node.format) {
          if (node.format & 1) { // Bold
            content = <strong>{content}</strong>;
          }
          if (node.format & 2) { // Italic
            content = <em>{content}</em>;
          }
          if (node.format & 4) { // Underline
            content = <u>{content}</u>;
          }
        }
        
        return <span key={key}>{content}</span>;
      
      case 'link':
        return (
          <a
            key={key}
            href={node.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
          >
            {node.children ? renderChildren(node.children) : node.url}
          </a>
        );
      
      case 'autolink':
        return (
          <a
            key={key}
            href={node.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
          >
            {node.children ? renderChildren(node.children) : node.url}
          </a>
        );
      
      default:
        // For unknown node types, try to render children if they exist
        if (node.children) {
          return <span key={key}>{renderChildren(node.children)}</span>;
        }
        return <span key={key}>{node.text || ''}</span>;
    }
  };

  if (!value || value.trim() === '') {
    return null;
  }

  return (
    <div className={`lexical-content ${className}`}>
      {renderLexicalContent(value)}
    </div>
  );
};

export default LexicalRenderer;
